import React, { useState } from 'react';
import {
  LineChart as RechartsLineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { LineChart, BarChart3, TrendingUp } from 'lucide-react';

const RevenueTrendChart = ({ data, trendData, chartType, period, onChartTypeChange, onPeriodChange }) => {
  const [showMovingAverage, setShowMovingAverage] = useState(true);

  // Use trendData if available, otherwise fall back to data
  const chartData = trendData?.data || data || [];

  if (!chartData || !chartData.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Revenue Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-64 bg-muted/50 rounded-lg">
            <p className="text-muted-foreground">No chart data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Revenue Trend</CardTitle>
          <div className="flex items-center gap-3">
            {trendData && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="show-moving-average"
                  checked={showMovingAverage}
                  onCheckedChange={setShowMovingAverage}
                />
                <label
                  htmlFor="show-moving-average"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Moving Average
                </label>
              </div>
            )}
            <Select value={period} onValueChange={onPeriodChange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Daily</SelectItem>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
              </SelectContent>
            </Select>
            <Select value={chartType} onValueChange={onChartTypeChange}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="line">
                  <div className="flex items-center gap-2">
                    <LineChart className="w-4 h-4" />
                    <span>Line</span>
                  </div>
                </SelectItem>
                <SelectItem value="bar">
                  <div className="flex items-center gap-2">
                    <BarChart3 className="w-4 h-4" />
                    <span>Bar</span>
                  </div>
                </SelectItem>
                <SelectItem value="area">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    <span>Area</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          {chartType === 'bar' ? (
            <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={(value) => `$${(value / 1000).toFixed(1)}K`} />
              <Tooltip
                contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                formatter={(value, name) => {
                  if (name === 'revenue') return `$${value.toLocaleString()}`;
                  if (name === 'moving_average') return `$${value.toLocaleString()} (MA)`;
                  return `$${value.toLocaleString()}`;
                }}
              />
              <Legend />
              <Bar dataKey="revenue" fill="#3b82f6" name="Revenue" />
            </BarChart>
          ) : chartType === 'area' ? (
            <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={(value) => `$${(value / 1000).toFixed(1)}K`} />
              <Tooltip
                contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                formatter={(value, name) => {
                  if (name === 'revenue') return `$${value.toLocaleString()}`;
                  if (name === 'moving_average') return `$${value.toLocaleString()} (MA)`;
                  return `$${value.toLocaleString()}`;
                }}
              />
              <Legend />
              <Area type="monotone" dataKey="revenue" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.6} name="Revenue" />
              {showMovingAverage && trendData && (
                <Line type="monotone" dataKey="moving_average" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" name="Moving Average" dot={false} />
              )}
            </AreaChart>
          ) : (
            <RechartsLineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 12 }} />
              <YAxis stroke="#6b7280" tick={{ fill: '#6b7280', fontSize: 12 }} tickFormatter={(value) => `$${(value / 1000).toFixed(1)}K`} />
              <Tooltip
                contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }}
                formatter={(value, name, props) => {
                  const formattedValue = `$${value.toLocaleString()}`;
                  if (name === 'revenue') {
                    const row = props.payload;
                    let tooltip = formattedValue;
                    if (row.growth_rate_percent !== null && row.growth_rate_percent !== undefined) {
                      tooltip += ` (${row.growth_rate_percent > 0 ? '+' : ''}${row.growth_rate_percent.toFixed(2)}%)`;
                    }
                    return tooltip;
                  }
                  if (name === 'moving_average') return `${formattedValue} (MA)`;
                  return formattedValue;
                }}
              />
              <Legend />
              <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} name="Revenue" dot={{ r: 4 }} activeDot={{ r: 6 }} />
              {showMovingAverage && trendData && (
                <Line type="monotone" dataKey="moving_average" stroke="#94a3b8" strokeWidth={2} strokeDasharray="5 5" name="Moving Average" dot={false} />
              )}
            </RechartsLineChart>
          )}
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default RevenueTrendChart;
